import type {
  WP_Checklist,
  ChecklistEntryUpdate,
  ChecklistEntryTasks,
  WP_ChecklistEntry,
} from '../model/checklist/checklist';
import { useContext, useState } from 'react';
import { AppContext } from '../state/AppContextProvider';
import { ActionType } from '../state/GlobalState';
import moment from 'moment';
import { useWP } from './WPHooks';
import { useLogger } from '../services/LogService';
import { ChecklistService } from '../services/ChecklistService';

export const useChecklists = () => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const [isFetch, setFetchChecklists] = useState(false);

  const fetchChecklists = () => {
    const user = state.user;
    if (user) {
      setFetchChecklists(false);

      wp.getChecklists(state.locale).then((checklists) => {
        const valid_checklist = checklists.filter((checklist) => {
          return (
            state.user && ChecklistService.isChecklistActive(user, checklist)
          );
        });

        valid_checklist.sort((a, b) => {
          return ChecklistService.checklistComparator(user, a, b);
        });

        dispatch({
          type: ActionType.SetAllChecklists,
          payload: valid_checklist,
        });
        setFetchChecklists(true);
      });
    }
  };

  return { isFetch: isFetch, fetchChecklists: fetchChecklists };
};

export const useChecklistEntry = () => {
  const { state, dispatch } = useContext(AppContext);

  const { wp } = useWP();

  const [isFetch, setFetchChecklistEntry] = useState(false);

  const [isPending, setPending] = useState(false);

  const [saveTask, setSaveTask] = useState<any>(undefined);

  const logger = useLogger('useChecklistEntry');

  const printEntry = (data: ChecklistEntryUpdate) => {
    return (
      '\n' +
      data.acf.entry_checklist_tasks
        .map(
          (t) =>
            `${t.entry_checklist_tasks_unique_id} >>> ${t.entry_checklist_tasks_value}`
        )
        .join('\n')
    );
  };

  const update = (
    checklistId: number,
    id: number,
    data: ChecklistEntryUpdate
  ) => {
    logger.debug('update() queuing task', printEntry(data));
    updateEntryState(checklistId, data);

    if (saveTask) {
      clearTimeout(saveTask);
    }

    setSaveTask(
      setTimeout(() => {
        setPending(true);
        logger.debug('update() running save task', printEntry(data));
        wp.putChecklistEntryAcf(id, data).then((data) => {
          updateEntryState(checklistId, data);
          setPending(false);
        });
      }, 1000)
    );
  };

  /* const updateEntryWithChecklist = (entry, checklist: WP_Checklist) => {
    if (entry?.acf?.entry_checklist_tasks && checklist?.acf?.checklist_tasks) {
      checklist.acf.checklist_tasks.forEach((task) => {
        // search checklist task into entry
        const foundTask = entry.acf.entry_checklist_tasks.find(
          (t) => task.checklist_tasks_key === t.entry_checklist_tasks_unique_id
        );
        if (foundTask) {
          // found: update the title
          foundTask.entry_checklist_tasks_title = task.checklist_tasks_title;
        } else {
          // create the new task
          const new_task = {
            entry_checklist_tasks_unique_id: task.checklist_tasks_key,
            entry_checklist_tasks_title: task.checklist_tasks_title,
            entry_checklist_tasks_value: false,
          };
          entry.acf.entry_checklist_tasks.push(new_task);
        }
      });
      // filter deprecated tasks
      entry.acf.entry_checklist_tasks = entry.acf.entry_checklist_tasks.filter(
        (entry_tasks) => {
          const id_task = entry_tasks.entry_checklist_tasks_unique_id;
          const foundTask = checklist.acf.checklist_tasks
            ? checklist.acf.checklist_tasks.find(
                (t) => t.checklist_tasks_key === id_task
              )
            : undefined;
          return foundTask ? true : false;
        }
      );
    }
    return entry;
  }; */

  const updateEntryState = (
    checklistId: number,
    entry: WP_ChecklistEntry | ChecklistEntryUpdate,
    checklist?: WP_Checklist
  ) => {
    const copy_checklist_entry = JSON.parse(
      JSON.stringify(state.allChecklistsEntries)
    );

    const old = ChecklistService.getEntry(
      checklistId,
      state.allChecklistsEntries
    ) || { acf: {} };

    // merge acf fields
    const mergedAcf = { ...old.acf, ...entry.acf };

    // merge all toghether
    copy_checklist_entry['entries_' + checklistId] = {
      ...old,
      ...entry,
      acf: mergedAcf,
    };

    dispatch({
      type: ActionType.SetAllChecklistsEntries,
      payload: copy_checklist_entry,
    });
  };

  const getEntry = (checklist: WP_Checklist) => {
    if (state.locale && state.user) {
      const user = state.user;
      // Get all checklists entry
      wp.getChecklistEntry(state.locale, checklist.id).then(
        (checklist_entry) => {
          if (checklist_entry.length === 0 && checklist !== undefined) {
            const checklist_tasks: Array<ChecklistEntryTasks> = [];
            (checklist?.acf?.checklist_tasks || []).map((task) => {
              let task_array = {
                entry_checklist_tasks_unique_id: task.checklist_tasks_key,
                entry_checklist_tasks_title: task.checklist_tasks_title,
                entry_checklist_tasks_value: false,
              };
              return checklist_tasks.push(task_array);
            });

            const checklist_start_day_ref = ChecklistService.getChecklistDay(
              user,
              checklist,
              'startdate'
            );
            const checklist_end_day_ref = ChecklistService.getChecklistDay(
              user,
              checklist,
              'enddate'
            );

            const valid_start_date =
              checklist.acf.checklist_start_before_after === 'after'
                ? moment(checklist_start_day_ref)
                    .add(parseInt(checklist.acf.checklist_start_number), 'd')
                    .format('YYYY-MM-DD')
                : moment(checklist_start_day_ref)
                    .subtract(
                      parseInt(checklist.acf.checklist_start_number),
                      'd'
                    )
                    .format('YYYY-MM-DD');
            const valid_end_date =
              checklist.acf.checklist_end_before_after === 'after'
                ? moment(checklist_end_day_ref)
                    .add(parseInt(checklist.acf.checklist_end_number), 'd')
                    .format('YYYY-MM-DD')
                : moment(checklist_end_day_ref)
                    .subtract(parseInt(checklist.acf.checklist_end_number), 'd')
                    .format('YYYY-MM-DD');

            const data: ChecklistEntryUpdate = {
              status: 'publish',
              title: '#' + state.user?.username + '-' + checklist.id + '',
              acf: {
                entry_checklist_user: state.user?.id,
                entry_checklist_post_id: checklist.id,
                entry_checklist_valid_date: valid_start_date,
                entry_checklist_end_valid_date: valid_end_date,
                entry_checklist_tasks: checklist_tasks,
                entry_checklist_status: 'in_progress',
              },
            };
            wp.postChecklistEntry(state.locale, data).then(
              (checklist_entry) => {
                updateEntryState(checklist.id, checklist_entry, checklist);
                setFetchChecklistEntry(true);
              }
            );
          } else {
            updateEntryState(checklist.id, checklist_entry[0], checklist);
            setFetchChecklistEntry(true);
          }
        }
      );
    }
  };

  return {
    getEntry: getEntry,
    isFetch: isFetch,
    isPending: isPending,
    update: update,
    updateEntryState: updateEntryState,
  };
};
